import {
  ATTRIBUTE_KEYS,
  type Attributes,
  type Element,
  type MonsterDef,
  type SectDef,
  type StatBlock,
} from '@jianghu/game-data';
import { COMBAT } from './config.js';

/** Fully derived combat statistics for any combatant (player or monster). */
export interface CombatStats {
  level: number;
  element: Element;
  maxHp: number;
  maxMp: number;
  atk: number;
  qiAtk: number;
  def: number;
  crit: number;
  critMult: number;
  accuracy: number;
  evasion: number;
  resist: Partial<Record<Element, number>>;
  /** Qi regenerated per second while in combat. */
  mpRegen: number;
}

export function attributesAtLevel(sect: SectDef, level: number, allocated?: Partial<Attributes>): Attributes {
  const out = {} as Attributes;
  for (const k of ATTRIBUTE_KEYS) {
    out[k] = sect.baseAttributes[k] + Math.floor(sect.growth[k] * (level - 1)) + (allocated?.[k] ?? 0);
  }
  return out;
}

/** Sums the rolled stat blocks of all equipped items. */
export function sumStatBlocks(blocks: readonly StatBlock[]): StatBlock {
  const out: StatBlock = {};
  for (const b of blocks) {
    for (const [k, v] of Object.entries(b) as [keyof StatBlock, number][]) {
      out[k] = (out[k] ?? 0) + v;
    }
  }
  return out;
}

function r1(v: number): number {
  return Math.round(v * 10) / 10;
}

export function derivePlayerStats(
  sect: SectDef,
  level: number,
  equipment: StatBlock,
  allocated?: Partial<Attributes>,
): CombatStats {
  const a = attributesAtLevel(sect, level, allocated);
  const l1 = level - 1;
  const eq = (k: keyof StatBlock): number => equipment[k] ?? 0;
  return {
    level,
    element: sect.element,
    maxHp: Math.round(80 + a.vit * 14 + l1 * 12 + eq('hp')),
    maxMp: Math.round(40 + a.ene * 10 + l1 * 6 + eq('mp')),
    atk: r1(4 + a.str * 1.6 + l1 * 1.5 + eq('atk')),
    qiAtk: r1(4 + a.ene * 1.6 + l1 * 1.5 + eq('qiAtk') + eq('atk') * 0.4),
    def: r1(a.vit * 0.8 + l1 + eq('def')),
    crit: Math.min(COMBAT.maxCritChance, 0.05 + a.agi * 0.004 + eq('crit')),
    critMult: COMBAT.critMultiplier,
    accuracy: eq('accuracy'),
    evasion: Math.min(COMBAT.maxEvasion, a.agi * 0.003 + eq('evasion')),
    resist: {},
    mpRegen: r1(2 + a.ene * 0.15),
  };
}

export function deriveMonsterStats(def: MonsterDef): CombatStats {
  return {
    level: def.level,
    element: def.element,
    maxHp: def.maxHp,
    maxMp: 0,
    atk: def.atk,
    qiAtk: def.atk,
    def: def.def,
    crit: def.crit,
    critMult: COMBAT.critMultiplier,
    accuracy: 0,
    evasion: def.evasion,
    resist: {},
    mpRegen: 0,
  };
}

/** Single number summarising character strength, shown in the character panel. */
export function powerRating(s: CombatStats): number {
  return Math.round(
    s.atk * 2 + s.qiAtk * 2 + s.def * 3 + s.maxHp * 0.3 + s.maxMp * 0.1 + s.crit * 400 + s.evasion * 300,
  );
}
